import { createSupabaseServerClient } from "@/lib/api";

export type GameEventPoint = {
  id: number;
  name: string;
  slug: string | null;
  sale_page_url: string | null;
  start_ts: number;
  end_ts: number;
};

export async function getGameEventsServer(appId: number): Promise<GameEventPoint[]> {
  const supabase = await createSupabaseServerClient();

  const { data, error } = await supabase
    .schema("analytics")
    .from("steam_event_games")
    .select("event_id, steam_events!inner(id, name, slug, sale_page_url, start_ts, end_ts)")
    .eq("app_id", appId)
    .not("steam_events.start_ts", "is", null)
    .not("steam_events.end_ts", "is", null);

  if (error) {
    console.error("[getGameEvents] Supabase error:", error);
    throw new Error(`Failed to fetch game events: ${error.message}`);
  }

  // PostgREST returns the embedded row under the relation name; supabase-js
  // widens it, so narrow it here instead of reaching for `any`.
  type EventJoinRow = { steam_events: GameEventPoint | null };

  return ((data ?? []) as unknown as EventJoinRow[])
    .map((row) => row.steam_events)
    .filter((e): e is GameEventPoint => Boolean(e));
}

/**
 * Per-appid request cache, shared by every chart block on the page.
 *
 * Each history block (CCU, top sellers, wishlist, followers, reviews, price)
 * mounts its own `useGameEvents` to draw the same festival markers, and the
 * fetches use `cache: "no-store"`, so nothing deduplicated them: a real Chrome
 * session on prod fired FOUR identical `/api/games/730/events` requests per
 * page view, and five on a game that also renders the price chart.
 *
 * Storing the promise (not the value) collapses the concurrent burst too — the
 * blocks mount within the same tick, so the later ones await the first one's
 * in-flight request. Entries live for the SPA session; sale windows change on
 * the scale of days, and the map holds one small array per game visited.
 *
 * Same shape as `previewCache` in GameHoverCard.tsx.
 */
const eventsCache = new Map<number, Promise<GameEventPoint[]>>();

async function fetchGameEvents(appId: number): Promise<GameEventPoint[]> {
  const response = await fetch(`/api/games/${appId}/events`, {
    cache: "no-store",
  });

  const payload = (await response.json()) as {
    events?: GameEventPoint[];
    error?: string;
  };

  if (!response.ok) {
    throw new Error(payload.error ?? "Failed to fetch game events");
  }

  return payload.events ?? [];
}

/**
 * Note: this deliberately takes NO `RequestInit`. The request is shared between
 * every chart block on the page, so forwarding one caller's AbortSignal would
 * cancel it for all the others the moment that block unmounted.
 * `useGameEvents` already ignores a late resolve via its `cancelled` flag, and
 * the payload is a handful of rows.
 */
export async function getGameEvents(appId: number): Promise<GameEventPoint[]> {
  const inFlight = eventsCache.get(appId);
  if (inFlight) return inFlight;

  // On failure, drop the entry so a later mount can retry instead of
  // replaying the rejection forever.
  const promise = fetchGameEvents(appId).catch((err) => {
    eventsCache.delete(appId);
    throw err;
  });

  eventsCache.set(appId, promise);
  return promise;
}
